import Card from "components/Card/Card";
import {
  ControlledDateInput,
  ControlledInput,
} from "components/Card/ControlledInput";
import { Checkbox, MaskedInput, TextInput } from "components/inputs";
import ControlledMultiSelect from "components/inputs/Select/ControlledMultiSelect";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import SectionBreak from "components/SectionBreak";
import { FM_SERVICE_TYPES } from "constants/SettingsConstants";
import { GENDERS } from "constants/FormConstants";
import DateOfInjuryForm from "pages/files/DateOfInjuryForm";
import { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { INSURANCE_TYPES, STATES } from "../shared/IntakeConstants";
import type { FormData } from "../shared/IntakeTypes";

type ClaimantIntakeFormProps = {
  handleFieldChange: (
    field: string,
    value:
      | string
      | number
      | boolean
      | Array<{ value: string; label: string }>
      | FormData["dates_of_injury"],
  ) => Promise<void>;
};

const ClaimantIntakeForm = ({ handleFieldChange }: ClaimantIntakeFormProps) => {
  const {
    control,
    register,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<FormData>();

  const datesOfInjury = watch("dates_of_injury");
  const services = watch("services");

  const hasFutureMedicalService = useMemo(() => {
    return services?.some((service) =>
      FM_SERVICE_TYPES.includes(service.value),
    );
  }, [services]);

  useEffect(() => {
    register("dates_of_injury");
  }, [register]);

  useEffect(() => {
    // register fields without immediate validation
    if (datesOfInjury && datesOfInjury.length > 0) {
      datesOfInjury.forEach((doi, doiIndex) => {
        register(`dates_of_injury.${doiIndex}.date`);
        register(`dates_of_injury.${doiIndex}.claim_numbers`);

        if (doi.claim_numbers && doi.claim_numbers.length > 0) {
          doi.claim_numbers.forEach((_, claimIndex) => {
            register(
              `dates_of_injury.${doiIndex}.claim_numbers.${claimIndex}.number`,
            );
          });
        }
      });
    }
  }, [datesOfInjury, register]);

  const getErrors = () => {
    return errors;
  };

  return (
    <Card
      heading="Claimant Information"
      cardNumber={2}
      style={{ columnSpan: "all" }}
    >
      <DateOfInjuryForm
        initialClaimNumbers={
          datesOfInjury?.flatMap((doi, doiIndex) =>
            (doi.claim_numbers || []).map((claim, claimIndex) => ({
              id: claimIndex,
              date_of_injury_id: doiIndex,
              number: claim.number,
              file: 0,
            })),
          ) || []
        }
        initialDatesOfInjury={
          datesOfInjury?.map((doi, index) => ({
            ...doi,
            id: index,
            file: 0,
          })) || []
        }
        errors={getErrors()}
        onChange={async (data) => {
          const currentValue = getValues("dates_of_injury");

          // only update if we have data and it's different
          // this prevents infinite loop of updating the same data
          if (data?.length > 0 || (currentValue && currentValue?.length > 0)) {
            // merge new data with existing accident information to prevent data loss,
            // since the date of injury form will overwrite the existing data if we resubmit ie. navigate back to Claimant Info section
            const mergedData = data.map((newItem, index) => {
              const existingItem = currentValue?.[index] || {};

              // preserve all existing accident information fields
              return {
                ...existingItem, // keep all existing fields (jurisdiction, wcmsa_zip_code, etc.)
                date: newItem.date, // update with new date
                claim_numbers: newItem.claim_numbers, // update with new claim numbers
              };
            });

            const isEqual =
              JSON.stringify(currentValue) === JSON.stringify(mergedData);

            if (!isEqual) {
              setValue("dates_of_injury", mergedData, {
                shouldValidate: false,
                shouldDirty: true,
                shouldTouch: true,
              });

              await handleFieldChange("dates_of_injury", mergedData);
            }
          }
        }}
      />
      <SectionBreak />
      <ControlledInput
        control={control}
        name="claimantFirstName"
        label="First Name"
        errors={errors}
        maxLength={100}
        onSave={(value) => handleFieldChange("claimantFirstName", value)}
        componentType={TextInput}
      />

      <ControlledInput
        control={control}
        name="claimantMiddleInitial"
        label="Middle Initial"
        errors={errors}
        maxLength={1}
        onSave={(value) => handleFieldChange("claimantMiddleInitial", value)}
        componentType={TextInput}
      />

      <ControlledInput
        control={control}
        name="claimantLastName"
        label="Last Name"
        errors={errors}
        maxLength={100}
        onSave={(value) => handleFieldChange("claimantLastName", value)}
        componentType={TextInput}
      />

      <ControlledMultiSelect<FormData>
        control={control}
        name="insuranceType"
        label="Insurance Types"
        errors={errors}
        options={INSURANCE_TYPES || []}
        // onSave={handleInsuanceTypeChange}
        onSave={async (options: Array<{ value: string; label: string }>) => {
          await handleFieldChange("insuranceType", options);
        }}
      />

      <ControlledSelect<FormData>
        control={control}
        name="claimantGender"
        label="Gender"
        errors={errors}
        options={GENDERS}
        onSave={async (option) => {
          if (option) {
            setValue("claimantGender", String(option.value));
            await handleFieldChange("claimantGender", option.value);
          }
        }}
      />

      <ControlledInput
        control={control}
        name="claimantSSN"
        label="SSN (xxx-xx-xxxx)"
        errors={errors}
        maxLength={11}
        onSave={(value) => handleFieldChange("claimantSSN", value)}
        componentType={MaskedInput}
        mask="ssn"
      />

      <ControlledDateInput
        control={control}
        name="claimantBirthDate"
        label="Birth Date"
        errors={errors}
        onSave={(value) => handleFieldChange("claimantBirthDate", value)}
        allowFutureDates={false}
      />

      <ControlledInput
        control={control}
        name="claimantAddress"
        label="Address"
        errors={errors}
        maxLength={100}
        onSave={(value) => handleFieldChange("claimantAddress", value)}
        componentType={TextInput}
      />

      <ControlledInput
        control={control}
        name="claimantCity"
        label="City"
        errors={errors}
        maxLength={100}
        onSave={(value) => handleFieldChange("claimantCity", value)}
        componentType={TextInput}
      />

      <ControlledSelect<FormData>
        control={control}
        name="claimantState"
        label="State"
        errors={errors}
        options={STATES}
        onSave={async (option) => {
          if (option) {
            setValue("claimantState", String(option.value));
            await handleFieldChange("claimantState", option.value);
          }
        }}
      />

      <ControlledInput
        control={control}
        name="claimantZipCode"
        label="Zip Code"
        errors={errors}
        maxLength={10}
        onSave={(value) => handleFieldChange("claimantZipCode", value)}
        componentType={MaskedInput}
        mask="zipcode"
      />

      <ControlledInput
        control={control}
        name="claimantMedicareID"
        label="Medicare ID"
        errors={errors}
        maxLength={12}
        onSave={(value) => handleFieldChange("claimantMedicareID", value)}
        componentType={TextInput} // this doenst work with MaskedInput properly?
      />

      <Checkbox
        {...register("isClaimantMedicareEligible", {
          onChange: () =>
            handleFieldChange(
              "isClaimantMedicareEligible",
              getValues("isClaimantMedicareEligible"),
            ),
        })}
        label="Medicare Eligible"
      />

      <Checkbox
        {...register("reasonableExpectation", {
          onChange: () =>
            handleFieldChange(
              "reasonableExpectation",
              getValues("reasonableExpectation"),
            ),
        })}
        label="Reasonable Expectation"
      />

      <Checkbox
        {...register("isRepresented", {
          onChange: () =>
            handleFieldChange("isRepresented", getValues("isRepresented")),
        })}
        label="Is Represented"
      />

      <ControlledInput
        control={control}
        name="claimantCounsel"
        label="P/C Firm Name"
        errors={errors}
        maxLength={100}
        onSave={(value) => handleFieldChange("claimantCounsel", value)}
        componentType={TextInput}
      />

      <ControlledInput
        control={control}
        name="attorneyName"
        label="Attorney Name"
        errors={errors}
        maxLength={100}
        onSave={(value) => handleFieldChange("attorneyName", value)}
        componentType={TextInput}
      />

      <ControlledInput
        control={control}
        name="attorneyAddress"
        label="Attorney Address"
        errors={errors}
        maxLength={100}
        onSave={(value) => handleFieldChange("attorneyAddress", value)}
        componentType={TextInput}
      />

      <ControlledInput
        control={control}
        name="attorneyPhone"
        label="Attorney Phone"
        errors={errors}
        onSave={(value) => handleFieldChange("attorneyPhone", value)}
        componentType={MaskedInput}
        mask="phone"
      />

      {hasFutureMedicalService && (
        <>
          <SectionBreak />

          <Checkbox
            {...register("settlement_reached", {
              onChange: () =>
                handleFieldChange(
                  "settlement_reached",
                  getValues("settlement_reached"),
                ),
            })}
            label="Settlement Reached"
          />

          <ControlledInput
            control={control}
            name="estimated_settlement_amount"
            label="Estimated Settlement Amount"
            errors={errors}
            onSave={(value) =>
              handleFieldChange("estimated_settlement_amount", value)
            }
            componentType={MaskedInput}
            mask="dollar"
          />

          <ControlledInput
            control={control}
            name="estimated_medical_settlement_value"
            label="Estimated Medical Settlement Value"
            errors={errors}
            onSave={(value) =>
              handleFieldChange("estimated_medical_settlement_value", value)
            }
            componentType={MaskedInput}
            mask="dollar"
          />

          <ControlledInput
            control={control}
            name="reserve_settlement_amount"
            label="Settlement Amount"
            errors={errors}
            onSave={(value) =>
              handleFieldChange("reserve_settlement_amount", value)
            }
            componentType={MaskedInput}
            mask="dollar"
          />

          <ControlledInput
            control={control}
            name="tentative_settlement_minimum"
            label="Tentative Settlement Minimum"
            errors={errors}
            onSave={(value) =>
              handleFieldChange("tentative_settlement_minimum", value)
            }
            componentType={MaskedInput}
            mask="dollar"
          />

          <ControlledInput
            control={control}
            name="tentative_settlement_maximum"
            label="Tentative Settlement Maximum"
            errors={errors}
            onSave={(value) =>
              handleFieldChange("tentative_settlement_maximum", value)
            }
            componentType={MaskedInput}
            mask="dollar"
          />
        </>
      )}

      <SectionBreak />

      <Checkbox
        {...register("awarded_ssd", {
          onChange: () =>
            handleFieldChange("awarded_ssd", getValues("awarded_ssd")),
        })}
        label="Awarded SSD"
      />

      <Checkbox
        {...register("applied_ssd", {
          onChange: () =>
            handleFieldChange("applied_ssd", getValues("applied_ssd")),
        })}
        label="Applied SSD"
      />
    </Card>
  );
};

export default ClaimantIntakeForm;
